import type {
  ICardExpirationDateElement,
  ICardNumberElement,
  ICardVerificationCodeElement,
} from '@basis-theory/react-elements'
import { useCallback } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { snakeToCamel } from '@utils/format.utils'

import {
  BasisTheoryClientError,
  CardDetail,
  useBasisTheoryClients,
} from '@hooks/useBasisTheoryClients'

export type TokenizedCardFlow = 'saved' | 'new'

export const tokenizedCardIdField = snakeToCamel('tokenized_card_id')
export const tokenizedCardIntentField = snakeToCamel('is_tokenized_card_id_intent')
export const tokenizedCardAuthenticationField = snakeToCamel('tokenized_card_authentication')

export interface TokenizedCard {
  tokenizedCardId: string
  isIntent: boolean
  authentication?: string
}

export const useTokenizedCard = () => {
  const apiClient = useApiClient(ClientApiClient)
  const { createCardTokenIntent, updateTokenCvc, btClient } = useBasisTheoryClients()

  const prepare = useCallback(
    async (params: {
      flow: TokenizedCardFlow
      selectedSavedCardTokenId?: string | null
      savedCardCvcEl?: ICardVerificationCodeElement | null
      newCard: {
        numberEl?: ICardNumberElement | null
        expEl?: ICardExpirationDateElement | null
        cvcEl?: ICardVerificationCodeElement | null
      }
    }): Promise<TokenizedCard | { error: string }> => {
      try {
        if (params.flow === 'saved') {
          if (!params.selectedSavedCardTokenId) return { error: 'missing_saved_card' }
          if (!params.savedCardCvcEl) return { error: 'missing_saved_cvc' }

          let tokenIdResponse
          try {
            tokenIdResponse = await apiClient.getBasisTheoryCardTokenId(
              params.selectedSavedCardTokenId
            )
          } catch {
            return { error: 'saved_card_verify_failed' }
          }

          try {
            const updateResponse = await updateTokenCvc({
              tokenId: tokenIdResponse.tokenId,
              cvcEl: params.savedCardCvcEl,
            })

            return {
              tokenizedCardId: params.selectedSavedCardTokenId,
              isIntent: false,
              authentication:
                tokenIdResponse.authentication ||
                ('card' in updateResponse
                  ? (updateResponse?.card as CardDetail)?.authentication
                  : ''),
            }
          } catch {
            return { error: 'saved_cvc_verify_failed' }
          }
        }

        const { numberEl, expEl, cvcEl } = params.newCard
        if (!numberEl || !expEl || !cvcEl) return { error: 'missing_new_card_fields' }

        const intent = await createCardTokenIntent({ numberEl, expEl, cvcEl })
        return { tokenizedCardId: intent.id, isIntent: true, authentication: intent.authentication }
      } catch (e: any) {
        if (e instanceof BasisTheoryClientError && e.type === 'missing-key') {
          return { error: 'missing_bt_key' }
        }
        return { error: 'unknown' }
      }
    },
    [apiClient, createCardTokenIntent, updateTokenCvc]
  )

  return { prepare, btClient }
}
